/**
 * 음성 저장 사용량 — 외부 API 탭의 넷째 보기 (2026-09-21 · 시안 ⑥).
 *
 * ## ★★★ 이것이 없으면 한도는 쓸모가 없다
 *
 * 서버는 음성 조각(WAV)을 **안 지운다** — 엔진 꽂는 날 전사할 원본이 필요하고 보관본(MP3 48k)은
 * 손실이라 못 쓴다. 조각은 `voice_chunks` 에 살아 **개인 5GiB 쿼터에 안 잡히므로**, 여기가
 * **유일하게 보이는 자리**다. 한도에 닿으면 **녹음이 멈추고 알린다**(`413 VOICE_STORAGE_FULL`).
 *
 * ## ★★ 전 회사를 한 번에
 *
 * 콘솔은 여러 회사를 보는 화면이라 회사마다 부르면 **사람당 분당 버킷**에 걸린다 — 날씨 위치에서
 * 겪은 그것이다. 그래서 `/admin/voice/storage/all` 하나로 받고, **날씨 위치와 같은 회사 줄** 얼개로 그린다.
 *
 * ## 404 는 «오류»가 아니라 «상태»다
 *
 * ✅ 라우트는 **섰다**(2026-09-24). 그래도 이 갈래를 남겨 둔다 — 배포가 **chat-server 먼저 ·
 * 콘솔 나중**이라 그 사이 창이 늘 생기기 때문이다.
 * ⛔ **404 를 빨간 오류로 그리면 운영자가 고장으로 읽는다.**
 *
 * ## ⛔ 한도를 바꾸는 칸을 두지 않는다
 *
 * 한도는 서버 환경변수(`VOICE_CHUNK_LIMIT_GIB`)다. 입력칸을 만들면 **눌러도 아무 일이 없다** —
 * 이 레포가 되풀이한 그 병이다. 읽기로만 보여 준다.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Globe } from '../../../icons';
import { listVoiceStorage, type VoiceStorageRow } from '../../../api/voiceStorage';
import { errText } from './ExternalApiEditModal';
import {
  formatGiB, usagePercent, barWidth, storageLevel,
  STORAGE_LEVEL_LABEL, daysSince, type StorageLevel,
} from './voiceStorageView';

const LEVEL_CLASS: Record<StorageLevel, string> = {
  ok: 'xa-badge--ok',
  near: 'xa-badge--warn',
  full: 'xa-badge--bad',
  unknown: 'xa-badge--muted',
};
const BAR_CLASS: Record<StorageLevel, string> = {
  ok: '', near: ' is-warn', full: ' is-bad', unknown: '',
};

function fmtDay(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

export interface VoiceStoragePanelProps {
  q: string;
  /** 「찬 곳만」 거르개. */
  onlyFull: boolean;
  onCount?: (n: { companies: number; full: number }) => void;
}

export function VoiceStoragePanel({ q, onlyFull, onCount }: VoiceStoragePanelProps) {
  /**
   * ⚠ 늘 «배열»로 쥔다 — `rows ?? []` 처럼 렌더마다 새 배열을 만들면 아래 `totals` 가 매번
   *   새로 셈해지고, `onCount` 효과가 매번 돌아 **부모 → 자식 → 부모**로 멈추지 않는다(실제로 겪음).
   */
  const [rows, setRows] = useState<VoiceStorageRow[]>([]);
  /** null = 아직 모름 · true = 서버가 준다 · false = 아직 안 준다(고장이 아니다). */
  const [ready, setReady] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await listVoiceStorage();
      setReady(r.ready);
      setRows(r.ready ? r.institutions : []);
    } catch (e: unknown) {
      // ⚠ 여기 오는 것은 404 «말고»다 — 그것은 진짜 탈이라 오류로 보여야 한다.
      setError(errText(e, '음성 저장 사용량을 불러오지 못했습니다'));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const totals = useMemo(() => ({
    companies: rows.length,
    full: rows.filter((r) => storageLevel(r.used_bytes, r.limit_bytes) === 'full').length,
  }), [rows]);
  useEffect(() => { onCount?.(totals); }, [onCount, totals]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (onlyFull && storageLevel(r.used_bytes, r.limit_bytes) !== 'full') return false;
      return !needle || r.name.toLowerCase().includes(needle) || (r.code ?? '').toLowerCase().includes(needle);
    });
  }, [rows, q, onlyFull]);

  if (loading) return <div className="cl-empty">불러오는 중…</div>;

  if (error) {
    return (
      <div className="xa-band xa-band--bad" role="alert">
        <AlertTriangle size={18} aria-hidden />
        <span className="xa-band__t"><b>{error}</b></span>
      </div>
    );
  }

  if (ready === false) {
    // ⛔ 빨간 오류가 아니다 — 아직 안 지어진 것이고, 서면 저절로 열린다.
    return (
      <div className="xa-band xa-band--info" role="status">
        <Globe size={18} aria-hidden />
        <span className="xa-band__t">
          <b>서버가 아직 음성 저장 사용량을 줄 줄 모릅니다</b>
          <span>
            전사 기능이 들어간 뒤에 이 값이 생깁니다 — <b>그때 이 화면이 저절로 열립니다.</b>
            그때까지도 <b>녹음과 저장은 되고 있습니다</b>(조각은 지워지지 않습니다).
          </span>
        </span>
      </div>
    );
  }

  return (
    <>
      {totals.full > 0 && (
        <div className="xa-band xa-band--bad" role="alert">
          <AlertTriangle size={18} aria-hidden />
          <span className="xa-band__t">
            <b>{totals.full}개 회사가 한도에 닿아 녹음이 멈췄습니다</b>
            <span>
              서버는 오래된 조각을 <b>지우지 않습니다</b> — 저절로 풀리지 않습니다.
              「가장 오래된 조각」이 무엇부터 볼지의 실마리입니다.
            </span>
          </span>
        </div>
      )}

      {shown.length === 0
        ? <div className="cl-empty">{onlyFull ? '한도에 닿은 회사가 없습니다' : '보여 줄 회사가 없습니다'}</div>
        : (
          <table className="cl-table xa-grid">
            <thead>
              <tr>
                <th>회사</th>
                <th style={{ width: '34%' }}>쓴 양</th>
                <th>회차 · 조각</th>
                <th>가장 오래된 조각</th>
                <th>상태</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const level = storageLevel(r.used_bytes, r.limit_bytes);
                const pct = usagePercent(r.used_bytes, r.limit_bytes);
                const days = daysSince(r.oldest_chunk_at);
                return (
                  <tr key={r.institution_id}>
                    <td>
                      <span className="xa-cell">
                        <b>{r.name}</b>
                        {r.code && <span>{r.code}</span>}
                      </span>
                    </td>
                    <td>
                      <span className="xa-usage__bar" style={{ display: 'block', marginBottom: 4 }}>
                        <i className={`xa-usage__fill${BAR_CLASS[level]}`} style={{ width: `${barWidth(r.used_bytes, r.limit_bytes)}%` }} />
                      </span>
                      <span className="xa-usage__num">
                        {formatGiB(r.used_bytes)} / {formatGiB(r.limit_bytes)}
                        {/* ⛔ 한도를 모르면 퍼센트를 지어내지 않는다. */}
                        {pct !== null && ` · ${pct}%`}
                      </span>
                    </td>
                    <td><span className="xa-usage__num">{r.session_count.toLocaleString()} · {r.chunk_count.toLocaleString()}</span></td>
                    <td>
                      <span className="xa-cell">
                        <b>{fmtDay(r.oldest_chunk_at)}</b>
                        {days !== null && <span>{days}일 전</span>}
                      </span>
                    </td>
                    <td><span className={`xa-badge ${LEVEL_CLASS[level]}`}>{STORAGE_LEVEL_LABEL[level]}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

      {/* ⛔ 한도를 바꾸는 칸은 없다 — 서버 설정이라 눌러도 아무 일이 없다. */}
      <p className="xa-hint">
        한도는 <b>서버 설정값</b>이라 이 화면에서는 바꿀 수 없습니다(읽기 전용).
        한도에 닿으면 <b>녹음이 멈추고 알립니다</b> — 서버가 오래된 조각을 지우지는 않습니다.
      </p>
    </>
  );
}
